import client from '@/api/client';
import { Field } from '@/components/Fields';
import { useMessage } from '@/components/Message';
import { TwoFactorSetupModal } from '@/features/twoFactor/TwoFactorSetupModal';
import { useAppDispatch, useErrorIndicator } from '@/hooks';
import { SignUpAllowed } from '@/types';
import { Button, Center, FormControl, HStack, Heading, Text, VStack } from '@chakra-ui/react';
import Link from 'next/link';
import { Form, Formik, FormikHelpers } from 'formik';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { sendVerificationEmail } from 'supertokens-web-js/recipe/emailverification';
import Session from 'supertokens-web-js/recipe/session';
import * as Yup from 'yup';
import { register } from './actions';

interface CredentialsRequest {
  email: string;
  password: string;
  organization: string;
}

export const RegisterView: React.FC = () => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const [isError, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { showError } = useErrorIndicator();
  const { showNavigationMessage } = useMessage();
  const [allowed, setAllowed] = useState<SignUpAllowed>({ emails: [], domains: [] });
  const [showTwoFactorModal, setShowTwoFactorModal] = useState(false);

  const handleSubmit = async (
    values: CredentialsRequest,
    { setSubmitting }: FormikHelpers<CredentialsRequest>,
  ) => {
    setSubmitting(true);
    try {
      const response = await dispatch(
        register({
          formFields: [
            {
              id: 'email',
              value: values.email.toLowerCase(),
            },
            {
              id: 'password',
              value: values.password,
            },
            {
              id: 'organization',
              value: values.organization,
            },
          ],
        }),
      ).unwrap();

      if (response.status === 'OK') {
        await sendVerificationEmail();
        setShowTwoFactorModal(true);
      } else {
        setError(true);
      }
    } catch (error) {
      showError('Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTwoFactorDone = async () => {
    setShowTwoFactorModal(false);
    await Session.signOut();
    showNavigationMessage({
      title: 'Verification Required',
      description: "Please check your email. We've sent you a verification link.",
      status: 'success',
    });
    router.push('/');
  };

  const RegisterSchema = Yup.object().shape({
    email: Yup.string()
      .min(2, 'Too Short!')
      .max(60, 'Too Long!')
      .required('Required')
      .test(
        'email-domain-validation',
        `Unsupported organization, please use your organization email address`,
        function (value) {
          if (allowed.emails.includes(value)) {
            return true;
          }
          const domain = value?.split('@')[1];
          return allowed.domains.includes(domain);
        },
      ),
    password: Yup.string()
      .min(8, 'Too Short!')
      .max(60, 'Too Long!')
      .matches(/^(?=.*[0-9])/, 'Password must contain at least one number')
      .required('Required'),
    organization: Yup.string()
      .required('Organization is required')
      .min(2, 'Organization name is too short')
      .max(100, 'Organization name is too long'),
  });

  useEffect(() => {
    setIsLoading(true);
    const fetchData = async () => {
      try {
        const { data } = await client.get(`/signUpAllowed`);
        if (data) {
          setAllowed(data);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };
    fetchData().finally(() => setIsLoading(false));
  }, []);

  return (
    <>
      <TwoFactorSetupModal
        isOpen={showTwoFactorModal}
        onClose={handleTwoFactorDone}
        onComplete={handleTwoFactorDone}
        onSkip={handleTwoFactorDone}
        allowSkip={true}
      />
      <Center>
        <VStack gap={12}>
          <Heading textAlign="center">Create a new account</Heading>
          <Formik
            initialValues={{ email: '', password: '', organization: '' }}
            onSubmit={handleSubmit}
            validationSchema={RegisterSchema}
          >
            {({ handleSubmit, isSubmitting, isValid, dirty, errors, touched }) => (
              <Form onSubmit={handleSubmit}>
                <FormControl alignItems="center" display="flex" flexDir="column">
                  <Field
                    type="email"
                    onDark
                    placeholder="Username"
                    name="email"
                    errors={errors.email}
                    touched={touched.email}
                    minW={{ base: 'unset', sm: '350px' }}
                    maxW="350px"
                    h="56px"
                    pl={3}
                    autoComplete="email"
                    full
                  />
                  <Field
                    type="text"
                    onDark
                    placeholder="Unit/Organization/Company"
                    name="organization"
                    errors={errors.organization}
                    touched={touched.organization}
                    minW={{ base: 'unset', sm: '350px' }}
                    maxW="350px"
                    h="56px"
                    pl={3}
                    autoComplete="organization"
                    full
                  />
                  <Field
                    type="password"
                    onDark
                    placeholder="Password"
                    name="password"
                    errors={errors.password}
                    touched={touched.password}
                    minW={{ base: 'unset', sm: '350px' }}
                    maxW="350px"
                    h="56px"
                    pl={3}
                    autoComplete="new-password"
                    full
                  />

                  {isError && (
                    <Text mb="30px" color="red" justifySelf="flex-start">
                      This email already exists. Please sign in instead.
                    </Text>
                  )}
                  <Button
                    fontWeight="semibold"
                    variant="primary"
                    size="xl"
                    w="full"
                    type="submit"
                    minW={{ base: 'unset', sm: '350px' }}
                    maxW="350px"
                    isDisabled={!isValid || !dirty || isSubmitting || isLoading}
                    isLoading={isSubmitting}
                  >
                    Sign up
                  </Button>
                </FormControl>
              </Form>
            )}
          </Formik>
          <HStack gap={1}>
            <Text>Already have an account?</Text>
            <Text as={Link} href="/" color="teal.500" cursor="pointer">
              Log in
            </Text>
          </HStack>
        </VStack>
      </Center>
    </>
  );
};
